import { vertexColor, mix, vec3, float, sin, max, min, dot, positionLocal, normalLocal, varying } from 'three/tsl';

import { uNight, uWarm, uTime, uOvercast, uLightning, uSunDir, uSunMix } from '../core/uniforms.js';

/**
 * @file Reusable TSL fragments. Everything in `render/materials.js` — and every
 * bespoke material a level or an actor builds — composes these, so the day/night
 * blend is defined in exactly ONE place.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * READ THIS BEFORE WRITING A BAKED MATERIAL (T4 / T5 / T6 / level authors)
 * ─────────────────────────────────────────────────────────────────────────────
 * `NodeMaterial.setupDiffuseColor()` in r185 does:
 *
 *     if ( this.vertexColors === true && geometry.hasAttribute('color') )
 *         colorNode = colorNode.mul( vertexColor() );
 *
 * i.e. setting `vertexColors: true` multiplies your `colorNode` by the `color`
 * attribute a SECOND time. Since {@link dayNightVertexColor} already reads
 * `vertexColor(0)` / `vertexColor(1)` explicitly, every baked material in this
 * project keeps `vertexColors` **false** (the default). Turning it on squares
 * the day channel and leaves the night channel untouched — a subtle, ugly bug
 * that only shows up at noon.
 *
 * `vertexColor(1)` reads the `color1` attribute and falls back to opaque WHITE
 * when the geometry does not have it, so these nodes are safe on un-baked
 * geometry (it just renders as its day channel with no night variant).
 *
 * @module engine/render/nodes
 */

/** @typedef {import('three/tsl').ShaderNodeObject<any>} TSLNode */

/**
 * Golden-hour grade applied to the DAY channel only. This is why dawn/dusk
 * needs no third bake: `uWarm` (driven by {@link import('../systems/DayNight.js').DayNight})
 * pushes the day colours toward amber while `uNight` is still ~0.
 *
 * Tuned to lift red and pull green/blue down — a "sun through dust" grade rather
 * than a sepia wash. It also loses a little total energy, because golden hour is
 * DIMMER as well as warmer and the day channel is baked at full noon intensity.
 *
 * DEVIATION from the T2 brief, which specified `[1.18, 1.02, 0.82]`: that is too
 * timid to read on screen — at `uWarm ≈ 0.6` it moved the image by a couple of
 * percent and dusk looked like an overcast noon. Verified visually at 06:30 and
 * 18:15 in `/play/daynight`.
 * @type {[number, number, number]}
 */
export const WARM_TINT = [1.16, 0.82, 0.48];

/**
 * `mix(vec3(1), WARM_TINT, uWarm)` — multiply the day channel by this.
 * @returns {TSLNode} vec3
 */
export function warmGrade() {
  return mix(vec3(1, 1, 1), vec3(WARM_TINT[0], WARM_TINT[1], WARM_TINT[2]), uWarm);
}

// ─────────────────────────────────────────────────────────────────────────────
// Weather grade — the one place cloud and lightning touch the world
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Rec.709 luma, for the overcast desaturation. A flat (1/3,1/3,1/3) average
 * makes a red car go MUCH darker than a blue one of the same brightness under
 * cloud, because it throws away the eye's green weighting.
 * @type {[number, number, number]}
 */
export const LUMA = [0.2126, 0.7152, 0.0722];

/** How far toward luma the day/night colour is pulled at `uOvercast = 1`. */
export const OVERCAST_DESAT = 0.25;

/**
 * Multiplied in AFTER the desaturation at `uOvercast = 1`: mean 0.83, i.e. ~17%
 * darker, with blue held ~11% over red so the darkening reads as COOL rather
 * than as somebody turning the exposure down.
 * @type {[number, number, number]}
 */
export const OVERCAST_GRADE = [0.79, 0.82, 0.88];

/**
 * Lightning. `gain` multiplies (so the image keeps its structure through the
 * flash instead of flattening to a card) and `add` lifts the floor (so a black
 * alley still visibly flashes). The tint is cool white, blue-biased.
 *
 * ⚠ TUNED AGAINST THE BLOOM, not against a colour picker. A full-screen lift is
 * the one effect in this project that can push EVERY pixel over the bloom
 * threshold at once, and `post.js` runs strength 1.15 / threshold 0.45 at night
 * — a night surface at 0.05 taken to ~0.24 reads as a hard flash and stays
 * under it, where the brief's literal "+1.6" put the whole frame at ~1.0 and
 * bloom turned the city into a white rectangle.
 * @type {[number, number, number]}
 */
export const LIGHTNING_TINT = [0.80, 0.88, 1.0];
/** @see LIGHTNING_TINT */
export const LIGHTNING_GAIN = 1.6;
/** @see LIGHTNING_TINT */
export const LIGHTNING_ADD = 0.16;

/**
 * Overcast + lightning, applied to an ALREADY day/night-blended colour.
 *
 * Folded into {@link dayNightVertexColor}, which is what every baked material in
 * the project reads — so static geometry, instanced props, rig parts, cars and
 * the ocean all get it for free. The two paths that do NOT go through
 * `dayNightVertexColor` (the sprite batches in `render/Billboards.js` and the
 * player's own `SpriteNodeMaterial` in `actors/Player.js`, both of which blend
 * a per-instance AMBIENT tint rather than a baked vertex colour) call this
 * directly on their blended tint. If you add a third such path, call it there
 * too — that is the whole contract.
 *
 * ⚠ IT MUST BE APPLIED EXACTLY ONCE. `dayNightInstanceTint` deliberately does
 * NOT call it: that helper is MULTIPLIED into `dayNightVertexColor`, and a
 * second grade would square the cloud.
 *
 * ⚠ AT `uOvercast = uLightning = 0` THIS IS AN EXACT IDENTITY, by construction:
 * `mix(c, grey, 0)` is `c`, `c * mix(1, gain, 0)` is `c * 1`, and `c + tint * 0`
 * is `c + 0`. Every existing scene therefore renders unchanged with no Weather
 * system present — see `core/uniforms.js`.
 *
 * @param {TSLNode} color vec3, post-day/night, linear, unclamped.
 * @returns {TSLNode} vec3
 */
export function weatherGrade(color) {
  const luma = dot(color, vec3(LUMA[0], LUMA[1], LUMA[2]));
  const desat = mix(color, vec3(luma), uOvercast.mul(float(OVERCAST_DESAT)));
  const clouded = desat.mul(
    mix(vec3(1, 1, 1), vec3(OVERCAST_GRADE[0], OVERCAST_GRADE[1], OVERCAST_GRADE[2]), uOvercast),
  );
  return clouded
    .mul(mix(float(1), float(LIGHTNING_GAIN), uLightning))
    .add(vec3(LIGHTNING_TINT[0], LIGHTNING_TINT[1], LIGHTNING_TINT[2]).mul(uLightning.mul(float(LIGHTNING_ADD))));
}

/**
 * The live sun term for a KEYED geometry: `dot(n, L) * visibility`.
 *
 * `n` is `normalLocal` and not a world normal ON PURPOSE — the merged city
 * geometry is baked in world space under an identity matrix, so the two are the
 * same thing there, and reading the attribute directly skips a normal-matrix
 * multiply on every vertex of the level. ⚠ That equality is exactly why a
 * ROTATED user (an instanced prop, a rig part, a car) may not use this path as
 * written; it would need `normalWorld` and its own keys. See `docs/rendering.md`.
 *
 * Visibility is the blend of the two resident keys, read from `color3`
 * (`.r` = key a, `.g` = key b, `Uint8` normalized). See `bake/sunKeys.js`.
 *
 * @returns {TSLNode} float, 0..1
 */
export function sunTermNode() {
  const ndl = max(dot(normalLocal, uSunDir), float(0));
  const vis = mix(vertexColor(3).r, vertexColor(3).g, uSunMix);
  return ndl.mul(vis);
}

/**
 * The received-shadow grade for an INSTANCE: `shared/props.js#sunTint`'s exact
 * formula, moved into the shader so the visibility can move with the sun.
 *
 * `k = floor + (1 - floor) * vis`, with the same cool shift the CPU version
 * ships — shadow is cooler as well as darker, because the sky is the only
 * light left in it. At `vis = 1` this is an exact identity (k = 1, both shift
 * terms cancel), so a fully sunlit instance renders byte-for-byte what it did.
 *
 * Evaluated per VERTEX and hoisted into a varying: `vis` is per-instance
 * constant, so interpolating the result is exact — and it keeps the fragment
 * stage on the right side of CLAUDE.md's decode rule.
 *
 * @param {TSLNode} vis float 0..1 — per-instance sun visibility.
 * @param {number} [floor=0.52] How dark full shadow gets. The CPU call sites
 *   used 0.5 (street props), 0.52 (roofs) and 0.55 (the default); one shader
 *   constant replaces all three, set to the middle value.
 * @returns {TSLNode} vec3 day-channel multiplier.
 */
export function sunShadeTint(vis, floor = 0.52) {
  const k = float(floor).add(float(1 - floor).mul(vis));
  return varying(
    vec3(
      k,
      k.mul(float(0.99).add(float(0.01).mul(vis))),
      min(float(1), k.mul(float(1.04).sub(float(0.04).mul(vis)))),
    ),
    'sunShadeTint',
  );
}

/**
 * THE runtime lighting blend: baked day channel (`color`, gold-graded) crossfaded
 * into the baked night channel (`color1`) by `uNight`, then WEATHER-GRADED.
 *
 * The weather grade sits OUTSIDE the day/night mix on purpose: cloud has to
 * desaturate a moonlit street exactly as it desaturates a sunlit one, and a
 * lightning flash has to lift both channels. Putting it inside would need a
 * third bake, which is the thing this whole engine exists not to do.
 *
 * Values may exceed 1 — that is deliberate, it is what feeds the bloom in
 * `render/post.js`. Do not clamp.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * `sun: true` — THE MOVING-SUN PATH, AND WHY IT IS A BUILD-TIME FLAG
 * ─────────────────────────────────────────────────────────────────────────────
 * With keys, `color` holds only the direction-free AMBIENT half of the day
 * channel and `color2` holds the sun tint; the sun is reassembled live:
 *
 *     day = (ambient + sunTint * dot(n,L) * vis) * warmGrade()
 *
 * ⚠ THIS CANNOT BE SWITCHED BY A UNIFORM, and the reason is a trap worth
 * knowing: `vertexColor(n)` falls back to opaque WHITE on geometry that lacks
 * the attribute. A global "sun keys on" uniform would therefore make every
 * UNKEYED geometry in a keyed level (props, rig parts, cars) evaluate
 * `1.0 * dot(n,L) * 1.0` and blow out to white in daylight. The flag has to
 * come from the caller, which is the only thing that knows whether ITS
 * geometry was keyed.
 *
 * At `sun: false` this is byte-identical to what it always was.
 *
 * @param {{sun?: boolean, dayMul?: TSLNode|null}} [o] `sun: true` only if the
 *   geometry carries `color2` + `color3` from a keyed bake. `dayMul` multiplies
 *   the DAY half only — the received-shadow hook for instanced users (cars pass
 *   {@link sunShadeTint} of their live per-instance visibility). It sits INSIDE
 *   the day/night mix on purpose: `mix(d, n, t) * mix(s, 1, t)` is NOT
 *   `mix(d*s, n, t)`, and the difference is a half-shaded night channel at
 *   twilight.
 * @returns {TSLNode} vec3, linear, unclamped.
 */
export function dayNightVertexColor(o = {}) {
  let day = o.sun
    ? vertexColor(0).rgb.add(vertexColor(2).rgb.mul(sunTermNode()))
    : vertexColor(0).rgb;
  if (o.dayMul) day = day.mul(o.dayMul);
  return weatherGrade(mix(day.mul(warmGrade()), vertexColor(1).rgb, uNight));
}

/**
 * The instanced-attribute variant, for `build/InstancedBatch.js`'s per-instance
 * `iColD` / `iColN` (day/night ambient sampled at placement time). Multiply this
 * INTO {@link dayNightVertexColor} — the geometry keeps its own bake, the
 * instance attributes only modulate it by where the copy was placed.
 *
 * The attribute nodes are passed in (never created here) so this module stays
 * ignorant of how the batch lays its buffers out.
 *
 * @param {TSLNode|null} attrD Per-instance DAY tint (vec3), e.g.
 *   `instancedBufferAttribute(iColD, 'vec3')`.
 * @param {TSLNode|null} [attrN] Per-instance NIGHT tint (vec3). Defaults to
 *   `attrD` (prop is lit the same at night, just darker overall).
 * @param {TSLNode|null} [shade] DAY-only multiplier — {@link sunShadeTint} of
 *   the instance's keyed visibility. Inside the mix for the same reason as
 *   `dayNightVertexColor`'s `dayMul`: shadow must not darken the night channel.
 * @returns {TSLNode} vec3
 */
export function dayNightInstanceTint(attrD, attrN = null, shade = null) {
  if (!attrD && !attrN) return shade ? mix(shade, vec3(1, 1, 1), uNight) : vec3(1, 1, 1);
  let d = attrD ?? attrN;
  const n = attrN ?? attrD;
  if (shade) d = d.mul(shade);
  return mix(d.mul(warmGrade()), n, uNight);
}

/**
 * Foliage sway: a `positionNode` that pushes vertices sideways by a sine driven
 * by `uTime`, scaled by height above the plant's base so trunks stay planted.
 *
 * The phase is a SMOOTH function of world XZ (two low-frequency sines), not a
 * hash: neighbouring vertices of one leaf card get near-identical phase, so a
 * card never tears, while plants metres apart wave out of step. Merged foliage
 * (level root at the origin) therefore needs no extra attribute at all.
 *
 * For instanced foliage, pass an explicit `phase` node (e.g. an instanced
 * attribute or `hash(instanceIndex)`) so every copy of the same geometry moves
 * differently.
 *
 * @param {Object} [o]
 * @param {number} [o.amount=0.09] Lateral metres of sway per metre of height.
 * @param {number} [o.freq=1.35] Radians/second.
 * @param {number} [o.base=0] Local Y that counts as "the ground" (no sway there).
 * @param {TSLNode|null} [o.phase] Explicit phase node (radians). Overrides the
 *   spatial default.
 * @param {TSLNode} [o.position=positionLocal] Position to displace.
 * @returns {TSLNode} vec3 — assign to `material.positionNode`.
 */
export function swayNode(o = {}) {
  const {
    amount = 0.09,
    freq = 1.35,
    base = 0,
    phase = null,
    position = positionLocal,
  } = o;

  const p = position;
  const ph = phase ?? sin(p.x.mul(0.37)).mul(2.1).add(sin(p.z.mul(0.29)).mul(1.7));
  // Height above the anchor, clamped at 0 so geometry below `base` never moves.
  const reach = max(p.y.sub(float(base)), float(0)).mul(amount);
  const t = uTime.mul(freq);

  const sx = sin(t.add(ph));
  // Second axis runs at an incommensurable rate: the tip traces a lissajous
  // instead of a straight line, which reads as wind rather than a metronome.
  const sz = sin(t.mul(0.73).add(ph.mul(1.31)).add(1.7));

  return p.add(vec3(sx.mul(reach), sz.mul(reach).mul(-0.12), sz.mul(reach).mul(0.62)));
}

/**
 * Hit flash: crossfade a colour toward white. `flashNode` is 0 normally and
 * spikes to ~1 on damage (T5/T6 decay it over 0.15s).
 *
 * @param {TSLNode|number} flashNode 0..1.
 * @param {TSLNode} [colorNode] Defaults to {@link dayNightVertexColor}.
 * @returns {TSLNode} vec3
 */
export function hitFlashMix(flashNode, colorNode = null) {
  const base = colorNode ?? dayNightVertexColor();
  return mix(base, vec3(1, 1, 1), flashNode);
}

/**
 * `1 - uNight * amount` — the standard "fade this out after dark" factor used by
 * decals and blob shadows (no sun, no hard shadow).
 * @param {number} [amount=0.7]
 * @returns {TSLNode} float
 */
export function nightFadeFactor(amount = 0.7) {
  return float(1).sub(uNight.mul(amount));
}
